import { keyframes } from '@emotion/react';
import { VisuallyHidden } from 'reakit';
import { variant } from 'styled-system';
import { Spinner as IconSpinner } from '@mos-cat/ds-icons';
import { createComponent, createHook } from 'reakit-system';
import { pxToRem } from '@mos-cat/ds-theme';
import type { BoxHTMLProps } from '../Box/Box';
import { useBox } from '../Box/Box';
import { cx, ecss } from '../reexports/emotion';
import type { StylesOptions } from '../hooks/useStyles';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { mode as getMode } from './variants';

type Mode = 'noSpace' | 'center' | 'withSpace';

export type LoaderProps = {
  overlayVisible: boolean;
  messageText?: string;
  iconColor?: string;
  iconSize?: string;
  mode?: Mode;
} & BoxHTMLProps &
  StylesOptions;

const spin = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(359deg)' },
});

const overlay = variant({
  prop: 'overlay',
  variants: {
    visible: { background: 'rgba(255, 255, 255, .8)' },
    invisible: { background: 'none' },
  },
});

type OverlayOptions = {
  overlay: 'visible' | 'invisible';
} & StylesOptions;

const useOverlay = createHook<OverlayOptions, BoxHTMLProps>({
  name: 'Overlay',
  keys: ['overlay'],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      position: 'absolute',
      size: '100%',
      top: 0,
      left: 0,
      zIndex: 1,
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        ecss(
          {
            label: 'LoaderOverlay',
          },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- не можем влиять на типы из styled-system
          overlay(options)
        )
      ),
    };
  },
});

const Overlay = createComponent({
  as: 'div',
  useHook: useOverlay,
  memo: true,
});

const useSpinnerWrapper = createHook<LoaderProps, BoxHTMLProps>({
  name: 'SpinnerWrapper',
  keys: ['iconSize', 'mode'],
  compose: [useBox],
  useOptions(options) {
    const { iconSize, mode } = options;

    return {
      ...options,
      position: 'relative',
      display: 'block',
      mx: 'auto',
      size: iconSize,

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- в либе styled-system variants возвращает any
      styles: {
        ...getMode({ mode }),
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        ecss({
          label: 'Spinner',
        })
      ),
    };
  },
});

export const SpinnerWrapper = createComponent({
  as: 'div',
  useHook: useSpinnerWrapper,
  memo: true,
});
const useSpinner = createHook<LoaderProps, BoxHTMLProps>({
  name: 'Spinner',
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      width: '100%',
      height: '100%',

      styles: {
        animation: `${spin} .75s linear 0s infinite normal none running`,
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        ecss({
          label: 'Spinner',
        })
      ),
    };
  },
});

export const Spinner = createComponent({
  as: IconSpinner,
  useHook: useSpinner,
  memo: true,
});

/**
 * @beta
 */

export function Loader({
  overlayVisible,
  messageText,
  iconColor = 'loaderColor',
  iconSize = pxToRem(40),
  mode = 'noSpace',
  ...props
}: LoaderProps) {
  useRequiredPropWarning(Boolean(overlayVisible), 'Loader', 'overlayVisible');
  return (
    <Overlay
      overlay={overlayVisible ? 'visible' : 'invisible'}
      color={iconColor}
      {...props}
    >
      <SpinnerWrapper iconSize={iconSize} mode={mode}>
        <Spinner />
      </SpinnerWrapper>
      <VisuallyHidden aria-live="polite">
        {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- здесь нужна проверка на пустую строку
          messageText || 'Идёт загрузка.'
        }
      </VisuallyHidden>
    </Overlay>
  );
}
